import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from "uuid";
import Grid from '@mui/material/Grid2';

import { 
  AttachedTable,
  UpgradableNumber, 
} from "@dndModels";
import { 
  ABILITY_SCORES_EXPANDED,
} from "@dndModels/Constants";
import { AbilityScoreExpandedType, AbilityScoreType, isDataType } from "@dndModels/Types";

import API from "@dndUtilities/system_api";

import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import SelectTableBox from "@dndComponents/select/SelectTableBox";
import SelectSourceBox from "@dndComponents/select/SelectSourceBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import CommonUtilities from "@utilities/common_utilities";
import SelectResourceBox from "@dndComponents/select/SelectResourceBox";
import AddButton from '@components/input/buttons/AddButton';

interface Props {
  value: UpgradableNumber;
  name: string;
  onChange: (changed: UpgradableNumber) => void; 
  slot_level?: number;
  color?: string;
  disabled?: boolean;
  log_it?: boolean;
  basic?: boolean;
  attached_table?: AttachedTable;
  quick_options?: (AbilityScoreType | "Prof" | "DC" | "DC STR" | "DC DEX" | "DC CON" | "DC INT" | "DC WIS" | "DC CHA" | "Attack STR" | "Attack DEX" | "Attack CON" | "Attack INT" | "Attack WIS" | "Attack CHA")[];
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function UpgradableNumberBox({ 
  value,
  name,
  onChange: ONCHANGE,
  slot_level = -1,
  color = "",
  disabled = false,
  log_it = false,
  basic = false,
  attached_table,
  quick_options = []
}: Props) {
  const [editing, setEditing] = useState("Base");
  // const [mod, setMod] = useState(true);
  const [is_basic, setBasic] = useState(basic || value.isBasic());
  const [reloading, setReloading] = useState(false);
  const [change_time, setChangeTime] = useState(0);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const onChange = (changed: UpgradableNumber) => {
    setChangeTime(common_util.get_time_number());
    ONCHANGE(changed);
  }

  const renderControl = () => {
    // if (name.includes("Armor")) {
    //   debugger;
    // }
    // console.log("Rendering");
    if (basic) {
      return renderBaseControl(name);
    } else if (is_basic) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ fontWeight: "bold" }}
            size={{
              xs: 12
            }}>
            { name }
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <ToggleButtonBox 
              name="Basic"
              value={is_basic} 
              disabled={disabled}
              onToggle={() => {
                if (!is_basic) {
                  const new_obj = value;
                  new_obj.makeBasic();
                  onChange(new_obj);
                }
                setBasic(!is_basic);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 8
            }}>
            { renderBaseControl(name) }
          </Grid>
          { !disabled && quick_options.length > 0 &&
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              { quick_options.map((opt, key) => {
                return (
                  <Grid
                    key={key}
                    size={{
                      xs: 4
                    }}>
                    <ButtonBox
                      name={opt}
                      onClick={() => {
                        // debugger;
                        const new_obj = value;
                        if (opt === "Prof") {
                          new_obj.add_prof_bonus_mult = 1;
                        } else if (opt === "DC") {
                          new_obj.add_spell_save_dc_mult = 1;
                        } else if (opt.startsWith("DC")) {
                          const pieces = opt.split(" ");
                          new_obj.base = 8;
                          new_obj.add_prof_bonus_mult = 1;
                          new_obj.add_stats_groups = [{ 
                            true_id: uuidv4().toString(),
                            add_ability_scores: [pieces[1] as AbilityScoreExpandedType],
                            mod_or_score: "Mod",
                            add_mult: 1,
                            multiple_type: "And"
                          }];
                          // new_obj.add_ability_mod_mult = 1;
                        } else if (opt.startsWith("Attack")) {
                          const pieces = opt.split(" ");
                          new_obj.add_prof_bonus_mult = 1;
                          // new_obj.add_ability_scores = [pieces[1] as AbilityScoreExpandedType];
                          new_obj.add_stats_groups = [{ 
                            true_id: uuidv4().toString(),
                            add_ability_scores: [pieces[1] as AbilityScoreExpandedType],
                            mod_or_score: "Mod",
                            add_mult: 1,
                            multiple_type: "And"
                          }];
                          // new_obj.add_ability_mod_mult = 1;
                        } else {
                          // new_obj.add_ability_scores = [opt as AbilityScoreExpandedType];
                          // new_obj.add_ability_mod_mult = 1;
                          
                          new_obj.add_stats_groups = [{ 
                            true_id: uuidv4().toString(),
                            add_ability_scores: [opt as AbilityScoreExpandedType],
                            mod_or_score: "Mod",
                            add_mult: 1,
                            multiple_type: "And"
                          }];
                        }
                        onChange(new_obj);
                        setBasic(false);
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          }
        </Grid>
      ); 
    } else {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            style={{ fontWeight: "bold" }}
            size={{
              xs: 12
            }}>
            { name }
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <ToggleButtonBox 
              name="Basic"
              value={is_basic} 
              disabled={disabled}
              onToggle={() => {
                if (!is_basic) {
                  const new_obj = value;
                  new_obj.makeBasic();
                  onChange(new_obj);
                }
                setBasic(!is_basic);
              }}
            />
          </Grid>
          { renderControls() }
          <Grid
            size={{
              xs: 12
            }}>
            { renderFormula() }
          </Grid>
        </Grid>
      ); 
    }
  }

  const renderControls = () => {
    if (is_basic) {
      return (
        <Grid
          size={{
            xs: 8
          }}>
          { renderBaseControl() }
        </Grid>
      );
    } else {
      const options = [
        "Base",
        "Char",
        "Source",
        "DC",
        "Spell Attack",
        "Non Magic Attack",
        "Magic DMG Bonus",
        "Non Magic DMG Bonus",
        "Prof",
        "Ability Score",
        "Spell Slot",
        "Table",
        "Formula Key",
        "Resource Dice Size",
        "Resource Dice Count",
        "Min",
        "Max",
        "Rounding",
        "Max Hit Points",
        "Walking Speed",
        "For Source",
        "Item Rarity",
        "Selected Rarity",
        // "For Proficiency",
        "Made with Slot Level",
        "Max Slot Size",
        "Selectable"
      ];
      if (slot_level > 0) {
        options.push("Upcast");
      }
      return (
        <>
          <Grid
            key="editing"
            size={{
              xs: 8
            }}>
            <SelectStringBox
              name="Editing"
              value={ editing }
              options={options}
              disabled={disabled}
              auto_sort
              onChange={(changed: string) => {
                setEditing(changed);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          {renderNotBasic()}
        </>
      );
    }
  }

  const renderFormula = () => {
    try {
      const formula = value.getFormula();
      return api.cleanFormula(formula);
    } catch (ex) {
      return null;
    }
  }

  const renderStatGroups = () => {
    return (
      <>
        <Grid size={12}>
          <AddButton
            label="Add Group"
            disabled={disabled}
            onClick={() => {
              const new_obj = value;
              new_obj.add_stats_groups.push({ 
                true_id: uuidv4().toString(),
                add_ability_scores: [],
                mod_or_score: "Mod",
                add_mult: 1,
                multiple_type: "And"
              });
              onChange(new_obj);
              setReloading(true);
            }}
          />
        </Grid>
        { value.add_stats_groups.map((group) => {
          return (
            <Grid size={12} key={group.true_id}>
              <Grid container spacing={0} direction="row">
                <Grid size={12}>
                  <SelectStringBox
                    name="Ability Score"
                    values={group.add_ability_scores}
                    options={ABILITY_SCORES_EXPANDED}
                    disabled={disabled}
                    multiple
                    onChange={(changed: AbilityScoreExpandedType[]) => {
                      const new_obj = value;
                      const g = new_obj.add_stats_groups.find(o => o.true_id === group.true_id);
                      if (g) {
                        g.add_ability_scores = changed;
                      }
                      onChange(new_obj);
                    }}
                  />
                </Grid>
                <Grid size={4}>
                  <SelectStringBox 
                    name="Modifier or Score"
                    value={group.mod_or_score} 
                    options={["Mod","Score"]}
                    disabled={disabled}
                    onChange={(changed: "Mod" | "Score") => {
                      const new_obj = value;
                      const g = new_obj.add_stats_groups.find(o => o.true_id === group.true_id);
                      if (g) {
                        g.mod_or_score = changed;
                      }
                      onChange(new_obj);
                    }}
                  />
                </Grid>
                <Grid size={4}>
                  <SelectStringBox
                    name="Multiple Type"
                    options={["Or", "And"]}
                    value={group.multiple_type}
                    disabled={disabled}
                    auto_sort
                    onChange={(changed: "Or" | "And") => {
                      const new_obj = value;
                      const g = new_obj.add_stats_groups.find(o => o.true_id === group.true_id);
                      if (g) {
                        g.multiple_type = changed;
                      }
                      onChange(new_obj);
                    }}
                  />
                </Grid>
                <Grid size={4}>
                  <StringBox
                    name={ group.mod_or_score === "Mod" ? `Ability Modifiers` : `Ability Scores` }
                    value={ `${group.add_mult}` }
                    type="number"
                    disabled={disabled}
                    change_time={change_time}
                    onBlur={(changed: string) => {
                      const new_obj = value;
                      const g = new_obj.add_stats_groups.find(o => o.true_id === group.true_id);
                      if (g) {
                        g.add_mult = +changed;
                      }
                      onChange(new_obj);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </>
    );
  }

  const renderNotBasic = () => {
    switch (editing) {
      case "Base":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            { renderBaseControl() }
          </Grid>
        );
      case "Char":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Char. Level Multiplier"
              value={`${value.add_char_level_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_char_level_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Made with Slot Level":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Made with Slot Level Multiplier"
              value={`${value.add_made_with_slot_level_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_made_with_slot_level_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Source":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Source Level Multiplier"
              value={`${value.add_source_level_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_source_level_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "For Source":
        return (
          <Grid size={12}>
            <SelectSourceBox
              type={value.source_type}
              id={value.source_id}
              onChange={(changed: { type?: string, id?: string }) => {
                const new_obj = value;
                new_obj.source_type = changed.type && isDataType(changed.type) ? changed.type : "";
                new_obj.source_id = changed.id ? changed.id : "";
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Spellcasting Class":
        return (
          <Grid size={6}>
            <SelectObjectBox
              name="Spellcasting Class"
              data_type="game_class"
              value={`${value.spellcasting_class_id}`}
              disabled={disabled}
              auto_sort
              onChange={(changed: string) => {
                const new_obj = value;
                new_obj.spellcasting_class_id = changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Max Slot Size":
        return (
          <Grid size={6}>
            <SelectObjectBox
              name="Max Slot Size for Class"
              data_type="game_class"
              extra_options={["Any"]}
              value={`${value.max_slot_for}`}
              disabled={disabled}
              auto_sort
              onChange={(changed: string) => {
                const new_obj = value;
                new_obj.max_slot_for = changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "DC":
        return (
          <Grid size={12}>
            <StringBox
              name="DC Multiplier"
              value={`${value.add_spell_save_dc_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_spell_save_dc_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Spell Attack":
        return (
          <Grid size={12}>
            <StringBox
              name="Spell Attack Multiplier"
              value={`${value.add_spell_attack_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_spell_attack_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Non Magic Attack":
        return (
          <Grid size={12}>
            <StringBox
              name="Non Magic Attack Multiplier"
              value={`${value.add_non_magic_attack_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_non_magic_attack_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Magic DMG Bonus":
        return (
          <Grid size={12}>
            <StringBox
              name="Magic DMG Bonus Multiplier"
              value={`${value.add_magic_dmg_bonus_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_magic_dmg_bonus_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Non Magic DMG Bonus":
        return (
          <Grid size={12}>
            <StringBox
              name="Non Magic DMG Bonus Multiplier"
              value={`${value.add_non_magic_dmg_bonus_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_non_magic_dmg_bonus_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Prof":
        return (
          <Grid size={12}>
            <StringBox
              name="Prof. Bonus Multiplier"
              value={`${value.add_prof_bonus_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_prof_bonus_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      // case "Ability Score":
      //   return (
      //     <>
      //       <Grid size={12}>
      //         <SelectStringBox
      //           name="Ability Score"
      //           values={value.add_ability_scores}
      //           options={ABILITY_SCORES_EXPANDED}
      //           disabled={disabled}
      //           multiple
      //           onChange={(changed: AbilityScoreExpandedType[]) => {
      //             const new_obj = value;
      //             new_obj.add_ability_scores = changed;
      //             onChange(new_obj);
      //           }}
      //         />
      //       </Grid>
      //       <Grid size={4}>
      //         <ToggleButtonBox 
      //           name="Modifier"
      //           value={mod} 
      //           disabled={disabled}
      //           onToggle={() => {
      //             const new_obj = value;
      //             if (!mod) {
      //               new_obj.add_ability_mod_mult = new_obj.add_ability_score_mult;
      //               new_obj.add_ability_score_mult = 0;
      //             } else {
      //               new_obj.add_ability_score_mult = new_obj.add_ability_mod_mult;
      //               new_obj.add_ability_mod_mult = 0;
      //             }
      //             onChange(new_obj);
      //             setMod(!mod);
      //           }}
      //         />
      //       </Grid>
      //       <Grid size={4}>
      //         <SelectStringBox
      //           name="Multiple Type"
      //           options={["Or", "And"]}
      //           value={value.multiple_type}
      //           disabled={disabled}
      //           auto_sort
      //           onChange={(changed: "Or" | "And") => {
      //             const new_obj = value;
      //             new_obj.multiple_type = changed;
      //             onChange(new_obj);
      //           }}
      //         />
      //       </Grid>
      //       <Grid size={4}>
      //         <StringBox
      //           name={ mod ? `Ability Modifiers` : `Ability Scores` }
      //           value={ mod ? `${value.add_ability_mod_mult}` : `${value.add_ability_score_mult}` }
      //           type="number"
      //           disabled={disabled}
      //           change_time={change_time}
      //           onBlur={(changed: string) => {
      //             const new_obj = value;
      //             if (mod) {
      //               new_obj.add_ability_mod_mult = +changed;
      //             } else {
      //               new_obj.add_ability_score_mult = +changed;
      //             }
      //             onChange(new_obj);
      //           }}
      //         />
      //       </Grid>
      //     </>
      //   );
      case "Ability Score":
        return renderStatGroups();
      case "Max Hit Points":
        return (
          <Grid size={12}>
            <StringBox
              name="Max HP Multiplier"
              value={`${value.add_hp_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_hp_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Walking Speed":
        return (
          <Grid size={12}>
            <StringBox
              name="Walking Speed Multiplier"
              value={`${value.add_walking_speed_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_walking_speed_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Upcast":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Upcast Multiplier"
              value={`${value.add_upcast_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_upcast_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Spell Slot":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Spell Slot Multiplier"
              value={`${value.add_slot_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.add_slot_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Min":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Minimum"
              value={ value.min === null ? "" : `${value.min}` }
              disabled={disabled}
              type="number"
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                if (["","null"].includes(changed)) {
                  new_obj.min = null;
                } else {
                  new_obj.min = +changed;
                }
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Max":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Maximum"
              value={ value.max === null ? "" : `${value.max}` }
              disabled={disabled}
              type="number"
        change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                if (["","null"].includes(changed)) {
                  new_obj.max = null;
                } else {
                  new_obj.max = +changed;
                }
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Rounding":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name="Rounding"
              value={ value.rounding }
              options={["Standard","Up","Down"]}
              disabled={disabled}
              onChange={(changed: "Up" | "Down" | "Standard") => {
                const new_obj = value;
                new_obj.rounding = changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Formula Key":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Formula Key"
              value={ value.formula_key }
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.formula_key = changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Resource Dice Size":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <SelectResourceBox 
              name="Resource Dice Size"
              value={ value.resource_dice_size }
              uses_dice_only
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = value;
                new_obj.resource_dice_size = changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Resource Dice Count":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <SelectResourceBox 
              name="Resource Dice Size"
              value={ value.resource_dice_count }
              disabled={disabled}
              uses_dice_only
              onChange={(changed: string) => {
                const new_obj = value;
                new_obj.resource_dice_count = changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Item Rarity":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Item Rarity Multiplier"
              value={`${value.item_rarity_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.item_rarity_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Selected Rarity":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Selected Rarity Multiplier"
              value={`${value.selected_rarity_mult}`}
              type="number"
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.selected_rarity_mult = +changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Table":
        return (
          <Grid size={12}>
            <SelectTableBox
              type={value.table_type}
              id={value.table_id}
              field={value.table_field}
              attached_table={attached_table}
              onChange={(changed: { type: string, id: string, field: string }) => {
                const new_obj = value;
                if (changed.type === "" || changed.type === "Attached" || isDataType(changed.type)) {
                  new_obj.table_type = changed.type;
                }
                new_obj.table_id = changed.id;
                new_obj.table_field = changed.field;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      case "Selectable":
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Selectable Key"
              value={value.selectable_key}
              disabled={disabled}
              change_time={change_time}
              onBlur={(changed: string) => {
                const new_obj = value;
                new_obj.selectable_key = changed;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
    }
    return null;
  }

  const renderBaseControl = (name = "Base") => {
    return (
      <StringBox
        name={ name }
        value={`${value.base}`}
        type="number"
        disabled={disabled}
        change_time={change_time}
        onBlur={(changed: string) => {
          const new_obj = value;
          new_obj.base = +changed;
          onChange(new_obj);
        }}
      />
    );
  }

  if (color !== "") {
    return (
      <div style={{
        border: "1px solid #1C9AEF"
      }}>
        { renderControl() }
      </div>
    );
  } else {
    return renderControl();
  }
}
